import { createSelector } from './selectorWrapper'
import { pageSelectors, simpleSelectors } from './basicState'
import { TitleIdToAction, ActionFragment } from '../reducers/action'
import { getPlaybackIds } from '../utils/playbackActionHelper'
import { btfActions, playbackFirstChildSelector } from './actionFragment'
import { playerConfig, PlaybackContentLink, PlayerContentConfig } from '@shared'
import * as WpsRuntime from 'DVWebNodeWpsRuntime'
import { getPlaybackClickstreamRef } from '../utils/getPlaybackClickstreamRef'
import { TRAILER_REFTAG } from '../components/playback/trailer'
import { parametersSelector } from './requestContext'
import { Parameters } from 'DVWebNodeUtils'

export type PlaybackAsinToCanonicalId = { [offerAsin: string]: string }

export enum Autoplay {
  feature = 'Feature',
  trailer = 'Trailer',
}

export const playerConfigDataSelector = createSelector(
  [
    simpleSelectors.context,
    simpleSelectors.pageContext
  ],
  (
    {
      sessionID: sessionId,
      marketplaceID: marketplaceId,
      customerID: customerId,
      locale,
    },
    pageContext: WpsRuntime.atv.wps.PageContextModelProps
  ) => ({
      sessionId,
      marketplaceId,
      customerId,
      locale,
      playerBeta: false,
      useDefaultView: true,
      pageType: (global.__NATIVEAPP__ ? 'SPA' : (pageContext.os === 'Android' ? 'DetailAndroid' : 'Detail')) as playerConfig.PageType,
    } as playerConfig.PlayerConfigData)
)

const featureContentConfigSelector = createSelector(
  [playbackFirstChildSelector],
  (
    playbackData?: WpsRuntime.atv.wps.PlaybackActionDataProps
  ): PlaybackContentLink | undefined =>
    playbackData
      ? {
          playbackUrl: playbackData.playbackURL,
          signInUrl: playbackData.signInURL,
          contentConfig: {
            asin: playbackData.playbackID,
            trailer: false,
            position: playbackData.resumeTime || 0,
            ref: getPlaybackClickstreamRef(
              playbackData.contentType,
              playbackData.entitlementType
            ),
            videoType: playbackData.videoMaterialType,
          },
        }
      : undefined
)

const trailerContentConfigSelector = createSelector(
  [pageSelectors.trailer],
  (
    trailerFragment?: WpsRuntime.atv.wps.TrailerFragmentModelProps
  ): PlaybackContentLink | undefined =>
    trailerFragment
      ? {
          playbackUrl: trailerFragment.playbackURL,
          contentConfig: {
            asin: trailerFragment.playbackID,
            trailer: true,
            position: 0,
            ref: TRAILER_REFTAG,
            videoType: trailerFragment.videoMaterialType,
          },
        }
      : undefined
)

export const autoplaySelector = createSelector(
  [
    parametersSelector,
    featureContentConfigSelector,
    trailerContentConfigSelector,
    pageSelectors.restriction,
  ],
  (
    parameters: Parameters,
    featureConfig?: PlaybackContentLink,
    trailerConfig?: PlaybackContentLink,
    restrictionsFragment?: WpsRuntime.atv.wps.RestrictionFragmentModelProps,
  ): PlaybackContentLink | undefined => {
    if (restrictionsFragment && restrictionsFragment.blockedPageModel) {
      return undefined
    }

    const autoplay = parameters.autoplay && parameters.autoplay.value

    const result =
      autoplay === 'trailer'
        ? trailerConfig
        : autoplay === '1'
        ? featureConfig
        : undefined

    if (result && parameters.t) {
      result.contentConfig.position = parseInt(parameters.t.value as string, 10) || 0
    }

    return result
  }
)

export const whisperCacheDataSelector = createSelector(
  [featureContentConfigSelector, trailerContentConfigSelector],
  (
    featureContentConfig?: PlaybackContentLink,
    trailerContentConfigSelector?: PlaybackContentLink
  ): PlayerContentConfig | undefined => {
    const config = featureContentConfig || trailerContentConfigSelector
    return config && !config.signInUrl ? config.contentConfig : undefined
  }
)

export const playbackIdToCanonicalIdSelector = createSelector(
  [simpleSelectors.pageTitleId, pageSelectors.actionAtf, btfActions],
  (
    pageTitleId: string,
    atfAction: ActionFragment,
    btfActions: TitleIdToAction
  ) => {
    const result: PlaybackAsinToCanonicalId = {}
    Object.entries({ [pageTitleId]: atfAction, ...btfActions })
      .filter(([, value]) => Boolean(value))
      .forEach(([canonicalId, action]) => {
        const { playbackActions } = action!
        const playbackIds = getPlaybackIds(playbackActions)
        if (playbackIds) {
          playbackIds.forEach(asin => {
            result[asin] = canonicalId
          })
        }
      })
    return result
  }
)
